import './usp-highlight.scss';
import Swiper from 'swiper';
import { A11y, Navigation, Pagination, Keyboard } from 'swiper/modules';

class UspHighlight {
    constructor (element, options) {
        // Default options
        const defaults = {
            initAttr: 'data-usp-highlight',
            numberValue: 'number',
            sliderValue: 'slider'
        };

        // Settings
        this.settings = Object.assign({}, defaults, options);
        this.slider = Object.assign({}, defaults, options);
        this.sliderActive = false; // Tracks if the slider is active

        // Elements
        this.$root = element; // Root element for the component
        this.$$numbers = this.$root.querySelectorAll(`[${this.settings.initAttr}="${this.settings.numberValue}"]`); // Number elements
        this.$slider = this.$root.querySelector(`[${this.settings.initAttr}="${this.settings.sliderValue}"]`); // Slider element
        this.$progressbar = this.$slider?.querySelector('.swiper-pagination-progressbar-fill'); // Progressbar element within the slider
    }

    initialize () {
        // Set minimum width for number elements if there are multiple
        if (this.$$numbers.length > 1) {
            this.setNumbersMinWidth();
        }

        // Initialize the slider if it exists
        if (this.$slider) {
            this.handleSliderActivation();
        }

        // Handle window resize events
        window.resizeHandler.customFunctions.push(() => {
            if (this.$$numbers.length > 1) {
                this.setNumbersMinWidth();
            }

            if (this.$slider) {
                this.handleSliderActivation();
            }
        });
    }

    handleSliderActivation () {
        // Activate or deactivate the slider based on window width
        if (window.innerWidth < 768 && !this.sliderActive) {
            this.initSlider();
        } else if (window.innerWidth >= 768 && this.sliderActive) {
            this.destroySlider();
        }
    }

    initSlider () {
        this.sliderActive = true; // Set slider as active

        // Function to update the progress bar based on the active slide
        const updateProgressbar = (index, steps) => {
            const progress = 1 / steps.length * (index + 1);
            if (this.$progressbar) {
                this.$progressbar.style.transform = `scaleX(${progress})`;
            }
        };

        // Initialize Swiper slider with options
        this.slider = new Swiper(this.$slider, {
            modules: [A11y, Navigation, Pagination, Keyboard],
            slidesPerView: 'auto',
            lazyPreloadPrevNext: true,
            speed: 500,
            pagination: {
                el: '.swiper-pagination-fraction', // Element for fraction pagination
                type: 'fraction',
                clickable: true,
                formatFractionCurrent: (number) => {
                    return ('0' + number).slice(-2);
                },
                formatFractionTotal: (number) => {
                    return ('0' + number).slice(-2);
                }
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev'
            },
            keyboard: true,
            on: {
                init: (swiper) => {
                    updateProgressbar(swiper.activeIndex, swiper.snapGrid);
                },
                slideChangeTransitionStart: (swiper) => {
                    updateProgressbar(swiper.activeIndex, swiper.snapGrid);
                }
            }
        });

        // Update progress bar on resize
        window.resizeHandler.customFunctions.push(() => {
            if (this.sliderActive) {
                updateProgressbar(this.slider.activeIndex, this.slider.snapGrid);
            }
        });
    }

    destroySlider () {
        // Destroy the slider instance and reset state
        if (this.slider !== null) {
            this.slider.destroy(true, true);
            this.slider = null;
            this.sliderActive = false;
        }
    }

    setNumbersMinWidth () {
        // Calculate and set the minimum width for number elements
        let numbersMinWidth = 0;

        this.$$numbers.forEach($number => {
            if ($number.offsetWidth > numbersMinWidth) {
                numbersMinWidth = $number.offsetWidth;
            }
        });

        this.$root.style.setProperty('--numbers-min-width', `${numbersMinWidth}px`);
    }
}

export { UspHighlight };

// Initialize UspHighlight on content load
window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$uspHighlight = $context.querySelectorAll('[data-usp-highlight="root"]');
        for (let i = 0; i < $$uspHighlight.length; i++) {
            const $uspHighlight = $$uspHighlight[i];

            const $uspHighlightAPI = new UspHighlight($uspHighlight);
            $uspHighlightAPI.initialize();
        }
    }
});
